import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  where
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { 
  formatNumberWithCommas, 
  parseFormattedNumber, 
  formatAmount,
  useFormattedNumberInput 
} from './numberFormatter';

Modal.setAppElement('#root');

// Date formatter for DD MMM YYYY (e.g., 07 Oct 2025)
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

// Generate 8-character journalId from Firestore ID
const generateJournalId = (firestoreId) => {
  return firestoreId.substring(0, 8);
};

const JournalEntries = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [journalEntries, setJournalEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newEntry, setNewEntry] = useState({ 
    date: new Date().toISOString().split('T')[0],
    description: '',
    lines: [{ accountName: '', debit: '', credit: '' }],
    userName: userData ? `${userData.firstName} ${userData.lastName}` : ''
  });
  const [editingEntry, setEditingEntry] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [isFetchingEntries, setIsFetchingEntries] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const journalEntriesCollectionRef = collection(db, "journalEntries");
  const accountsCollectionRef = collection(db, "hotelAccounts");

  useEffect(() => {
    if (!user || !user.uid || !userData) {
      console.log("Waiting for user authentication or userData...");
      setLoading(true);
      return;
    }
    setLoading(false);
    fetchAccounts();
    fetchJournalEntries();
  }, [user, userData]);

  useEffect(() => {
    setNewEntry(prev => ({ ...prev, userName: userData ? `${userData.firstName} ${userData.lastName}` : '' }));
  }, [userData]);

  useEffect(() => {
    const filtered = journalEntries.filter((entry) => {
      const amount = formatAmount(getEntryAmount(entry.lines)).replace(/₦|,/g, '');
      const formattedDate = dateFormatter.format(new Date(entry.date)).replace(/,/g, '');
      const accountNames = entry.lines.map(line => line.accountName || '').join(' ').toLowerCase();
      return (
        entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (entry.userName && entry.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        formattedDate.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.date.includes(searchTerm) ||
        amount.includes(searchTerm.replace(/,/g, '')) ||
        accountNames.includes(searchTerm.toLowerCase()) ||
        (entry.journalId && entry.journalId.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    setFilteredEntries(filtered);
  }, [searchTerm, journalEntries]);

  const fetchAccounts = async () => {
    if (!user?.uid) {
      console.error("No user ID available for fetching accounts");
      setError("User not authenticated");
      setShowModal(true);
      return;
    }
    try {
      const q = query(accountsCollectionRef);
      const data = await getDocs(q);
      const accountList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setAccounts(accountList);
    } catch (error) {
      console.error("Failed to fetch accounts:", error.message, error.code);
      setError(`Failed to fetch accounts: ${error.message} (${error.code})`);
      setShowModal(true);
    }
  };

  const fetchJournalEntries = async () => {
    if (!user?.uid) {
      console.error("No user ID available for fetching journal entries");
      setError("User not authenticated");
      setShowModal(true);
      return;
    }
    setIsFetchingEntries(true);
    try {
      const q = query(journalEntriesCollectionRef);
      const data = await getDocs(q);
      const entryList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      entryList.sort((a, b) => new Date(b.date) - new Date(a.date));
      setJournalEntries(entryList);
      if (entryList.length === 0) {
        console.log("No journal entries found for user:", user.uid);
        setError("No journal entries found");
        setShowModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch journal entries:", error.message, error.code);
      setError(`Failed to fetch journal entries: ${error.message} (${error.code})`);
      setShowModal(true);
    } finally {
      setIsFetchingEntries(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEntry({ ...newEntry, [name]: value });
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...newEntry.lines];
    
    if (field === 'debit' || field === 'credit') {
      // Use the utility function for real-time comma formatting
      newLines[index][field] = formatNumberWithCommas(value);
    } else {
      newLines[index][field] = value;
    }
    
    setNewEntry({ ...newEntry, lines: newLines });
  };

  const getLineNumericValue = (value) => {
    return parseFormattedNumber(value);
  };

  const addLine = () => {
    setNewEntry({ ...newEntry, lines: [...newEntry.lines, { accountName: '', debit: '', credit: '' }] });
  };

  const removeLine = (index) => {
    const newLines = newEntry.lines.filter((_, i) => i !== index);
    setNewEntry({ ...newEntry, lines: newLines });
  };

  const validateDoubleEntry = (lines) => {
    const totalDebit = lines.reduce((sum, line) => {
      const debitValue = getLineNumericValue(line.debit);
      return sum + debitValue;
    }, 0);
    
    const totalCredit = lines.reduce((sum, line) => {
      const creditValue = getLineNumericValue(line.credit);
      return sum + creditValue;
    }, 0);
    
    return Math.abs(totalDebit - totalCredit) < 0.01;
  };

  const validateEntry = (entry) => {
    if (!entry.description.trim()) {
      setError("Description cannot be empty");
      return false;
    }
    
    if (!validateDoubleEntry(entry.lines)) {
      setError("Debits must equal credits");
      return false;
    }
    if (entry.lines.some(line => !line.accountName.trim())) {
      setError("All lines must have a valid account name");
      return false;
    }
    if (!user?.uid) {
      setError("User not authenticated");
      return false;
    }
    return true;
  };

  const handleAddEntry = async () => {
    // Prepare entry with numeric values
    const entryToSave = {
      ...newEntry,
      lines: newEntry.lines.map(line => ({
        accountName: line.accountName,
        debit: getLineNumericValue(line.debit),
        credit: getLineNumericValue(line.credit)
      }))
    };

    if (!validateEntry(entryToSave)) {
      setShowModal(true);
      return;
    }
    
    setUpdate(true);
    try {
      const docRef = await addDoc(journalEntriesCollectionRef, {
        ...entryToSave,
        userId: user.uid,
        userName: userData ? `${userData.firstName} ${userData.lastName}` : 'Unknown',
        createdAt: Timestamp.now(),
        journalId: ''
      });
      const journalId = generateJournalId(docRef.id);
      await updateDoc(doc(db, "journalEntries", docRef.id), { journalId });
      setSuccessMessage("Journal entry added successfully");
      fetchJournalEntries();
      resetNewEntry();
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Failed to add journal entry:", error.message, error.code);
      setError(`Failed to add journal entry: ${error.message} (${error.code})`);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateEntry = async () => {
    // Prepare entry with numeric values
    const entryToSave = {
      ...newEntry,
      lines: newEntry.lines.map(line => ({
        accountName: line.accountName,
        debit: getLineNumericValue(line.debit),
        credit: getLineNumericValue(line.credit)
      }))
    };

    if (!validateEntry(entryToSave)) {
      setShowModal(true);
      return;
    }
    
    setUpdate(true);
    try {
      const entryDoc = doc(db, "journalEntries", editingEntry.id);
      await updateDoc(entryDoc, {
        ...entryToSave,
        userId: user.uid,
        userName: userData ? `${userData.firstName} ${userData.lastName}` : 'Unknown',
        journalId: editingEntry.journalId || generateJournalId(editingEntry.id)
      });
      setSuccessMessage("Journal entry updated successfully");
      fetchJournalEntries();
      resetNewEntry();
      setEditingEntry(null);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Failed to update journal entry:", error.message, error.code);
      setError(`Failed to update journal entry: ${error.message} (${error.code})`);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetNewEntry = () => {
    setNewEntry({ 
      date: new Date().toISOString().split('T')[0],
      description: '',
      lines: [{ accountName: '', debit: '', credit: '' }],
      userName: userData ? `${userData.firstName} ${userData.lastName}` : ''
    });
  };

  const handleEditEntry = (entryToEdit) => {
    setEditingEntry({ ...entryToEdit, id: entryToEdit.id });
    setNewEntry({ 
      date: entryToEdit.date,
      description: entryToEdit.description,
      lines: entryToEdit.lines.map(line => ({
        accountName: line.accountName || (line.accountId ? accounts.find(a => a.id === line.accountId)?.name || '' : ''),
        debit: line.debit === 0 ? '' : formatNumberWithCommas(line.debit.toString()),
        credit: line.credit === 0 ? '' : formatNumberWithCommas(line.credit.toString())
      })),
      userName: entryToEdit.userName || (userData ? `${userData.firstName} ${userData.lastName}` : '')
    });
    setIsEditModalOpen(true);
  };

  const confirmDeleteEntry = (entryToDelete) => {
    setCurrentEntry(entryToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteEntry = async (id) => {
    setUpdate(true);
    try {
      const entryDoc = doc(db, "journalEntries", id);
      await deleteDoc(entryDoc);
      setSuccessMessage("Journal entry deleted successfully");
      fetchJournalEntries();
    } catch (error) {
      console.error("Failed to delete journal entry:", error.message, error.code);
      setError(`Failed to delete journal entry: ${error.message} (${error.code})`);
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  const getEntryAmount = (lines) => {
    const totalDebit = lines.reduce((sum, line) => sum + parseFloat(line.debit || 0), 0);
    return totalDebit;
  };

  if (loading) return <Loader text={"LOADING"} />;
  if (!userData) return <Loader text={"READING"} />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Journal Entries</h2>
            <Link to="#" className="home-link" onClick={() => fetchJournalEntries()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">            
            <div className="row justify-center align-center gap-15 add-buttons">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Entry</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by journal ID, description, created by, date (DD MMM YYYY or YYYY-MM-DD), amount, or account"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetchingEntries ? (
                  <p className="loading-text">Loading entries...</p>
                ) : filteredEntries.length === 0 ? (
                  <p className="loading-text">No entries match your search or no entries exist.</p>
                ) : (
                  filteredEntries.map((entryItem) => (
                    <div key={entryItem.id} className="class-item">
                      <div className="column gap-1">
                        <p>
                          <strong>{entryItem.description}</strong><br />                          
                          <span>By: {entryItem.userName || 'Unknown'}</span><br />
                          <span>Amount: {formatAmount(getEntryAmount(entryItem.lines))}</span><br />
                          <span className="size5 bold">ID: {entryItem.journalId || 'N/A'}</span><br />
                          <span className="size5 bold font-gold">{dateFormatter.format(new Date(entryItem.date))}</span>
                        </p>                        
                      </div>
                      <div className="row align-center justify-center gap-15">
                        <button className="list-btn" onClick={() => handleEditEntry(entryItem)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="list-btn delete-btn" onClick={() => confirmDeleteEntry(entryItem)}>
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Journal Entry"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Journal Entry</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="date" className="input-label">Date</label>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newEntry.date}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="description" className="input-label">Description</label>
          <textarea
            name="description"
            className="input-field"
            placeholder="Description"
            value={newEntry.description}
            onChange={handleInputChange}
            required
          />
          <h4>Entry Lines</h4>
          {newEntry.lines.map((line, index) => (
            <div className="column">              
              <label htmlFor="accountName" className="input-label">Select Account</label>
              <select
                value={line.accountName}
                onChange={(e) => handleLineChange(index, 'accountName', e.target.value)}
                className="input-field"
              >
                <option value="">Select Account</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.name}>{acc.name}</option>
                ))}
              </select>
              <div key={index} className="row gap-1">
                <div>
                  <label htmlFor="dedit" className="input-label">Debit</label>
                  <input
                    type="text"
                    placeholder="0"
                    value={line.debit}
                    onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                    className="input-field"
                  />
                  <hr />
                </div>
                <div>
                  <label htmlFor="credit" className="input-label">Credit</label>
                  <input
                    type="text"
                    placeholder="0"
                    value={line.credit}
                    onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                    className="input-field"
                  />
                </div>
                {newEntry.lines.length > 1 && (
                  <button type="button" className="remove-button" onClick={() => removeLine(index)}><i className="fas fa-trash-alt font-red"></i></button>
                )}                
              </div>              
            </div>
          ))}
          <button type="button" className="login-button" onClick={addLine}>Add Line</button>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddEntry}>Add Entry</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          resetNewEntry();
          setEditingEntry(null);
        }}
        contentLabel="Edit Journal Entry"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Journal Entry</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="date" className="input-label">Date</label>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newEntry.date}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="description" className="input-label">Description</label>
          <textarea
            name="description"
            className="input-field"
            placeholder="Description"
            value={newEntry.description}
            onChange={handleInputChange}
            required
          />
          <p>Entry Lines</p>
          {newEntry.lines.map((line, index) => (
            <div key={index} className="column">
              <label htmlFor="accountName" className="input-label">Select Account</label>
              <select
                value={line.accountName}
                onChange={(e) => handleLineChange(index, 'accountName', e.target.value)}
                className="input-field"
              >
                <option value="">Select Account</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.name}>{acc.name}</option>
                ))}
              </select>
              <div className="row gap-1">
                <div>
                  <label htmlFor="debit" className="input-label">Debit</label>
                  <input
                    type="text"
                    placeholder="0"
                    value={line.debit}
                    onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                    className="input-field"
                  />
                  <hr />
                </div>
                <div>
                  <label htmlFor="credit" className="input-label">Credit</label>
                  <input
                    type="text"
                    placeholder="0"
                    value={line.credit}
                    onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                    className="input-field"
                  />
                </div>
                {newEntry.lines.length > 1 && (
                <button type="button" className="remove-button" onClick={() => removeLine(index)}><i className="fas fa-trash-alt font-red"></i></button>
              )}
              </div>              
            </div>
          ))}
          <button type="button" className="login-button" onClick={addLine}>Add Line</button>
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              resetNewEntry();
              setEditingEntry(null);
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateEntry}>Update Entry</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Journal Entry"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Journal Entry</h2>
        {currentEntry && (
          <div>
            <p>Are you sure you want to delete <strong>{currentEntry.description}</strong> (Journal ID: {currentEntry.journalId || 'N/A'})?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeleteEntry(currentEntry.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default JournalEntries;